<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useAdminMail } from '@/composables/useMail'
import { handleAxiosError } from '@/composables/useAxiosError'
import type { MailAccount } from '@/types/dashboard/mail'
import { formatBytes } from '@/utils'
import QuotaProgressBar from '@/components/base/QuotaProgressBar.vue'
import MailTypeIcon from '@/components/dashboard/Mail/common/MailTypeIcon.vue'

const { t } = useI18n()
const router = useRouter()
const { listAllAccounts } = useAdminMail()

const loading = ref(true)
const accounts = ref<MailAccount[]>([])
const totalAccounts = ref(0)
const searchQuery = ref('')
const selectedType = ref<string>('all')
const page = ref(1)
const itemsPerPage = ref(25)
let searchDebounce: ReturnType<typeof setTimeout> | null = null

const typeOptions = [
  { value: 'all', label: t('dashboard.mail.accounts.filter.all') },
  { value: 'mailbox', label: t('dashboard.mail.accounts.filter.mailbox') },
  { value: 'alias', label: t('dashboard.mail.accounts.filter.alias') },
  { value: 'list', label: t('dashboard.mail.accounts.filter.list') },
  { value: 'shared', label: t('dashboard.mail.accounts.filter.shared') }
]

const totalPages = computed(() => Math.ceil(totalAccounts.value / itemsPerPage.value))

const fetchAccounts = async () => {
  loading.value = true
  try {
    const res = await listAllAccounts({
      limit: itemsPerPage.value,
      offset: (page.value - 1) * itemsPerPage.value,
      search: searchQuery.value || undefined,
      type: selectedType.value !== 'all' ? selectedType.value : undefined
    })
    if (res.data) {
      accounts.value = res.data.accounts ?? []
      totalAccounts.value = res.data.total ?? 0
    }
  } catch (error) {
    handleAxiosError(error as Error, { logPrefix: 'AdminMailAccounts' })
  } finally {
    loading.value = false
  }
}

const viewDetails = (account: MailAccount) => {
  router.push({ name: 'dashboardMailDetail', params: { id: String(account.id) } })
}

const getDomainFromEmail = (email: string): string => {
  const parts = email.split('@')
  return parts.length > 1 ? (parts[1] ?? '') : ''
}

const formatRelativeTime = (dateStr: string): string => {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return t('common.justNow')
  if (mins < 60) return t('common.minutesAgo', { n: mins })
  const hours = Math.floor(mins / 60)
  if (hours < 24) return t('common.hoursAgo', { n: hours })
  const days = Math.floor(hours / 24)
  return t('common.daysAgo', { n: days })
}

watch(searchQuery, () => {
  if (searchDebounce) clearTimeout(searchDebounce)
  searchDebounce = setTimeout(() => {
    page.value = 1
    fetchAccounts()
  }, 300)
})

watch(selectedType, () => {
  page.value = 1
  fetchAccounts()
})

watch(page, () => {
  fetchAccounts()
})

onMounted(() => {
  fetchAccounts()
})

onUnmounted(() => {
  if (searchDebounce) clearTimeout(searchDebounce)
})
</script>

<template>
  <div class="admin-mail-accounts">
    <div class="page-header">
      <div class="header-info">
        <h1>{{ t('dashboard.mail.admin.accounts.title') }}</h1>
        <p>{{ t('dashboard.mail.admin.accounts.subtitle') }}</p>
      </div>
      <button class="refresh-btn" :disabled="loading" @click="fetchAccounts">
        <v-icon icon="mdi-refresh" size="18" :class="{ spinning: loading }" />
      </button>
    </div>

    <!-- Filters -->
    <div class="filters-bar">
      <v-text-field
        v-model="searchQuery"
        :placeholder="t('dashboard.mail.accounts.search')"
        prepend-inner-icon="mdi-magnify"
        variant="outlined"
        density="compact"
        hide-details
        clearable
        class="search-input"
      />
      <div class="type-filter">
        <button
          v-for="opt in typeOptions"
          :key="opt.value"
          class="filter-btn"
          :class="{ active: selectedType === opt.value }"
          @click="selectedType = opt.value"
        >
          {{ opt.label }}
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading && accounts.length === 0" class="loading-state">
      <v-progress-circular indeterminate size="32" width="2" color="primary" />
    </div>

    <!-- Empty State -->
    <div v-else-if="accounts.length === 0" class="empty-state">
      <v-icon icon="mdi-email-off" size="48" />
      <h3>{{ t('dashboard.mail.accounts.empty.title') }}</h3>
    </div>

    <!-- Table -->
    <div v-else class="table-wrapper">
      <table class="accounts-table">
        <thead>
          <tr>
            <th>{{ t('dashboard.mail.admin.accounts.table.email') }}</th>
            <th>{{ t('dashboard.mail.admin.accounts.table.domain') }}</th>
            <th>{{ t('dashboard.mail.admin.accounts.table.type') }}</th>
            <th>{{ t('dashboard.mail.admin.accounts.table.usage') }}</th>
            <th>{{ t('dashboard.mail.admin.accounts.table.status') }}</th>
            <th>{{ t('dashboard.mail.admin.accounts.table.lastLogin') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="account in accounts" :key="account.id" class="clickable-row" @click="viewDetails(account)">
            <td>
              <div class="email-cell">
                <span class="email-name">{{ account.email }}</span>
              </div>
            </td>
            <td>
              <span class="domain-badge">{{ account.domain_name || getDomainFromEmail(account.email) }}</span>
            </td>
            <td>
              <div class="type-cell">
                <MailTypeIcon :type="account.type" :size="16" />
                <span>{{ t(`dashboard.mail.types.${account.type}`) }}</span>
              </div>
            </td>
            <td>
              <QuotaProgressBar
                v-if="account.quota_bytes"
                :used-bytes="account.used_bytes || 0"
                :total-bytes="account.quota_bytes"
                compact
              />
              <span v-else-if="account.used_bytes" class="usage-text">{{ formatBytes(account.used_bytes) }}</span>
              <span v-else class="no-quota">-</span>
            </td>
            <td>
              <v-chip
                :color="account.active ? 'success' : 'grey'"
                size="small"
                variant="tonal"
              >
                {{ account.active ? t('dashboard.mail.accounts.status.active') : t('dashboard.mail.accounts.status.inactive') }}
              </v-chip>
            </td>
            <td class="last-login-cell">
              <span v-if="account.last_login" :title="new Date(account.last_login).toLocaleString('de-DE')">
                {{ formatRelativeTime(account.last_login) }}
              </span>
              <span v-else class="no-login">—</span>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="pagination">
        <button class="page-btn" :disabled="page <= 1" @click="page--">
          <v-icon icon="mdi-chevron-left" size="18" />
        </button>
        <span class="page-info">{{ page }} / {{ totalPages }}</span>
        <button class="page-btn" :disabled="page >= totalPages" @click="page++">
          <v-icon icon="mdi-chevron-right" size="18" />
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.admin-mail-accounts {
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 24px;
}

.header-info {
  h1 {
    font-size: 1.5rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.95);
    margin: 0 0 4px;
  }

  p {
    font-size: 0.9rem;
    color: rgba(200, 218, 255, 0.6);
    margin: 0;
  }
}

.refresh-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(120, 163, 255, 0.1);
  border: 1px solid rgba(120, 163, 255, 0.2);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.8);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: rgba(120, 163, 255, 0.2);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.filters-bar {
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.search-input {
  flex: 1;
  min-width: 200px;
  max-width: 320px;
}

.type-filter {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.filter-btn {
  padding: 6px 14px;
  background: rgba(15, 23, 48, 0.5);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 6px;
  color: rgba(200, 218, 255, 0.7);
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(120, 163, 255, 0.1);
  }

  &.active {
    background: rgba(33, 150, 243, 0.15);
    border-color: rgba(33, 150, 243, 0.3);
    color: #64b5f6;
  }
}

.loading-state {
  display: flex;
  justify-content: center;
  padding: 60px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px;
  color: rgba(200, 218, 255, 0.5);

  h3 {
    margin-top: 16px;
    font-size: 1rem;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.7);
  }
}

.table-wrapper {
  background: rgba(15, 23, 48, 0.5);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
}

.accounts-table {
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    text-align: left;
    padding: 12px 16px;
  }

  th {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: rgba(200, 218, 255, 0.5);
    background: rgba(15, 23, 48, 0.3);
  }

  td {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.85);
    border-bottom: 1px solid rgba(120, 163, 255, 0.05);
  }

  tr:last-child td {
    border-bottom: none;
  }
}

.email-cell {
  display: flex;
  flex-direction: column;
}

.email-name {
  font-weight: 500;
}

.domain-badge {
  padding: 2px 8px;
  background: rgba(120, 163, 255, 0.1);
  border-radius: 4px;
  font-size: 0.8rem;
  font-family: 'JetBrains Mono', monospace;
}

.type-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.no-quota {
  color: rgba(200, 218, 255, 0.4);
}

.clickable-row {
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(120, 163, 255, 0.08);
  }
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 16px;
  border-top: 1px solid rgba(120, 163, 255, 0.08);
}

.page-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: rgba(120, 163, 255, 0.1);
  border: none;
  border-radius: 6px;
  color: rgba(200, 218, 255, 0.8);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: rgba(120, 163, 255, 0.2);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
}

.page-info {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.6);
}

/* Light Theme */
.v-theme--lightTheme {
  .header-info h1 {
    color: rgba(15, 23, 42, 0.95);
  }

  .filter-btn {
    background: rgba(255, 255, 255, 0.7);
    border-color: rgba(28, 110, 227, 0.1);
    color: rgba(15, 23, 42, 0.7);

    &.active {
      background: rgba(28, 110, 227, 0.1);
      border-color: rgba(28, 110, 227, 0.25);
      color: #1976d2;
    }
  }

  .table-wrapper {
    background: rgba(255, 255, 255, 0.7);
    border-color: rgba(28, 110, 227, 0.1);
  }

  .accounts-table {
    th {
      color: rgba(15, 23, 42, 0.6);
      background: rgba(15, 23, 42, 0.03);
    }

    td {
      color: rgba(15, 23, 42, 0.85);
    }
  }
}

/* Responsive */
@media (max-width: 768px) {
  .filters-bar {
    flex-direction: column;
  }

  .search-input {
    max-width: none;
  }

  .accounts-table {
    font-size: 0.85rem;

    th,
    td {
      padding: 10px 12px;
    }
  }
}
</style>

<style lang="scss">
.admin-mail-accounts .search-input .v-field {
  background: rgba(15, 23, 48, 0.5);
}

.v-theme--lightTheme .admin-mail-accounts .search-input .v-field {
  background: rgba(255, 255, 255, 0.8);
}
</style>
